import * as React from "react";

import { loadMoreCollaborateThreads } from "../api";
import { useCollaborateStore } from "../store/useCollaborateStore";

const NEAR_BOTTOM_PX = 160;

function shouldLoadMore(root: HTMLElement): boolean {
  const { scrollTop, scrollHeight, clientHeight } = root;
  const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
  const hasOverflow = scrollHeight > clientHeight + 1;

  if (!hasOverflow) {
    return true;
  }

  return distanceFromBottom <= NEAR_BOTTOM_PX;
}

/** Observe list scroll position and load the next thread page near the bottom. */
export function useCollaborateInfiniteScroll(
  scrollRootRef: React.RefObject<HTMLElement | null>,
  documentId?: string,
  resetKey?: string,
): React.RefObject<HTMLDivElement | null> {
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const threadCount = useCollaborateStore((s) => s.threadIds.length);
  const hasMore = useCollaborateStore((s) => s.threadsPagination.hasMore);
  const loadingMore = useCollaborateStore((s) => s.threadsPagination.loadingMore);
  const loadingInitial = useCollaborateStore((s) => s.threadsPagination.loadingInitial);
  const requestInFlightRef = React.useRef(false);

  React.useEffect(() => {
    requestInFlightRef.current = false;
  }, [resetKey, documentId]);

  React.useEffect(() => {
    const root = scrollRootRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore || loadingMore || loadingInitial) {
      return undefined;
    }

    const scheduleAfterPaint = (run: () => void) => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(run);
      });
    };

    const maybeLoadMore = () => {
      if (requestInFlightRef.current) {
        return;
      }
      const state = useCollaborateStore.getState().threadsPagination;
      if (!state.hasMore || state.loadingMore || state.loadingInitial) {
        return;
      }
      if (!shouldLoadMore(root)) {
        return;
      }

      requestInFlightRef.current = true;
      void loadMoreCollaborateThreads(documentId)
        .finally(() => {
          requestInFlightRef.current = false;
        })
        .then(() => {
          scheduleAfterPaint(() => {
            maybeLoadMore();
          });
        });
    };

    const onScroll = () => {
      maybeLoadMore();
    };

    root.addEventListener("scroll", onScroll, { passive: true });

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          maybeLoadMore();
        }
      },
      { root, threshold: 0 },
    );
    observer.observe(sentinel);

    scheduleAfterPaint(() => {
      maybeLoadMore();
    });

    return () => {
      root.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, [
    scrollRootRef,
    documentId,
    hasMore,
    loadingMore,
    loadingInitial,
    resetKey,
    threadCount,
  ]);

  return sentinelRef;
}
